import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { ClientLedger } from '../types';
import { registerCalibriFonts, setCalibriFont } from '../utils/fontLoader';

export const formatDate = (date: any): string => {
  if (!date) return 'N/A';
  try {
    // Handle Firestore Timestamp
    if (date.toDate && typeof date.toDate === 'function') {
      const convertedDate = date.toDate();
      if (convertedDate instanceof Date && !isNaN(convertedDate.getTime())) {
        return convertedDate.toLocaleDateString();
      }
    }
    // Handle Date objects or strings
    const parsedDate = new Date(date);
    if (parsedDate instanceof Date && !isNaN(parsedDate.getTime())) {
      return parsedDate.toLocaleDateString();
    }
    return 'N/A';
  } catch (error) {
    console.warn('Invalid date format:', date, error);
    return 'N/A';
  }
};

// Types for ledger configuration
export type LedgerType = 'client' | 'company';

export interface LedgerConfig {
  type: LedgerType;
  orientation?: 'portrait' | 'landscape';
  includeShippingDetails?: boolean;
  includeDaysOutstanding?: boolean;
  includeRemarks?: boolean;
}

// Default configurations for each ledger type
const LEDGER_CONFIGS: Record<LedgerType, LedgerConfig> = {
  client: {
    type: 'client',
    orientation: 'landscape',
    includeShippingDetails: true,
    includeDaysOutstanding: true,
    includeRemarks: true
  },
  company: {
    type: 'company',
    orientation: 'portrait',
    includeShippingDetails: false,
    includeDaysOutstanding: false,
    includeRemarks: false
  }
};

export const generateLedgerPDF = async (
  ledger: ClientLedger, 
  invoices: any[], 
  selectedLogisticsCompany?: string,
  ledgerType: LedgerType = 'client',
  clientType?: 'import' | 'export' | 'both'
): Promise<jsPDF> => {
  const config = { ...LEDGER_CONFIGS[ledgerType] };
  
  // For import clients, hide days outstanding and remarks
  if (clientType === 'import') {
    config.includeDaysOutstanding = false;
    config.includeRemarks = false;
  }
  
  const doc = new jsPDF({ orientation: config.orientation });

  // Register Calibri fonts
  await registerCalibriFonts(doc);

  // Set up the page
  const pageWidth = (doc.internal.pageSize as any).getWidth
    ? (doc.internal.pageSize as any).getWidth()
    : (doc.internal.pageSize as any).width;
  const margin = 5;
  let yPosition = margin;

  // Determine logistics company - use selected one if provided, otherwise from first invoice
  const firstInvoice = invoices.length > 0 ? invoices[0] : null;
  const logisticsCompany = selectedLogisticsCompany || firstInvoice?.logisticsCompany || 'BARQ CARGO SYSTEM PVT LTD.';
  const isVector = logisticsCompany === 'VECTOR LOGISTICS';
  
  // Add company logo
  try {
    const logoPath = isVector ? '/assets/vector_logo.png' : '/assets/barq_logo-min.png';
    if (isVector) {
      doc.addImage(logoPath, 'PNG', margin, yPosition, 60, 17);
    } else {
      // For Barq logo, maintain aspect ratio
      doc.addImage(logoPath, 'PNG', margin, yPosition, 50, 20);
    }
  } catch (error) {
  }

  // Add company name on the right side with underline
  doc.setFontSize(13);
  setCalibriFont(doc, 'bold');
  doc.setTextColor(2, 18, 61);
  const companyNameLeftX = pageWidth - margin - doc.getTextWidth(logisticsCompany);
  const companyNameOffsetY = 6;
  doc.text(logisticsCompany, companyNameLeftX, yPosition + companyNameOffsetY);
  
  // Add underline under company name
  const companyNameWidth = doc.getTextWidth(logisticsCompany);
  (doc as any).setDrawColor(2, 18, 61);
  doc.setLineWidth(0.75);
  doc.line(companyNameLeftX, yPosition + companyNameOffsetY + 1, companyNameLeftX + companyNameWidth, yPosition + companyNameOffsetY + 1);

  // Add company address and contact details below the company name
  doc.setTextColor(2, 18, 61);
  doc.setFontSize(9);
  setCalibriFont(doc, 'normal');
  
  const addressLines = isVector ? [
    'Office M-7, M Floor, Khan Arcade,',
    'Firdous Market, Gulberg III,',
    'Lahore, Punjab, Pakistan',
    'Tel 042-35644728, 35881129', 
  ] : [
    'Office M-7, M Floor, Khan Arcade, Firdous Market,',
    'Gulberg III, Lahore, Punjab, Pakistan',
    'Tel 042-35644728, 35881129', 
  ];
  
  // Position address text to start directly under the company name
  let addressY = yPosition + companyNameOffsetY + 6;
  addressLines.forEach(line => {
    doc.text(line, companyNameLeftX, addressY);
    addressY += 3.5;
  });

  yPosition = addressY + 8; // Add more gap after address details

  // Add client/company name as title
  doc.setFontSize(12);
  setCalibriFont(doc, 'bold');
  doc.setTextColor(0, 0, 0); // Reset to black
  doc.text(ledger.clientName, margin, yPosition);

  // Add Statement Of Account and As Of date on the same line
  yPosition += 5;
  doc.setFontSize(10);
  setCalibriFont(doc, 'bold');
  doc.text('STATEMENT OF ACCOUNT', margin, yPosition);

  // Add "As Of:" date on the right side of the same line
  doc.setFontSize(9);
  setCalibriFont(doc, 'bold');
  const asOfText = `AS OF:  ${new Date().toLocaleDateString()}`;
  const asOfTextWidth = doc.getTextWidth(asOfText);
  doc.text(asOfText, pageWidth - margin - asOfTextWidth, yPosition);

  // Create a map of invoice data for quick lookup
  const invoiceMap = new Map(invoices.map(inv => [inv.id, inv]));
  
  // Create a map of invoice ID to invoice type for "both" client type handling
  const invoiceTypeMap = new Map(invoices.map(inv => [inv.id, inv.invoiceType]));

  // Separate opening balance entries from regular entries
  const openingBalanceEntries = ledger.entries.filter(entry => 
    entry.type === 'adjustment' && entry.description === 'Opening Balance'
  );
  const regularEntries = ledger.entries.filter(entry => 
    !(entry.type === 'adjustment' && entry.description === 'Opening Balance')
  );

  // Sort regular entries by date
  regularEntries.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Combine opening balance entries first, then regular entries
  const sortedEntries = [...openingBalanceEntries, ...regularEntries];

  // Prepare ledger data for the table
  let runningBalance = 0; // For client ledger running balance calculation
  const ledgerData = sortedEntries.map(entry => {
    const invoice = entry.invoiceId ? invoiceMap.get(entry.invoiceId) : null;

    // Calculate running balance for client ledger
    if (config.type === 'client') {
      runningBalance += entry.credit - entry.debit;
    }

    const effectiveBalance = config.type === 'client' 
      ? (typeof (entry as any).balance === 'number' ? (entry as any).balance : runningBalance)
      : entry.balance;

    // For opening balance entries, don't show date
    const isOpeningBalance = entry.type === 'adjustment' && entry.description === 'Opening Balance';
    
    // Base columns that are common to both types
    const baseColumns = [
      isOpeningBalance ? '' : (entry.date?.toLocaleDateString ? entry.date.toLocaleDateString() : formatDate(entry.date)), // Date (empty for opening balance)
      invoice ? (invoice.invoiceNumber || '') : '', // Invoice #
    ];

    // Add shipping-specific columns for client ledger
    if (config.includeShippingDetails && invoice) {
      const blNumberOrAwb = invoice.blNumber || invoice.awbNumber || '';
      
      // Enhanced function to handle multiple container numbers or package counts
      const normalizeContainerPackage = (data: any) => {
        if (!data) return '';
        
        // If it's already an array, join with line breaks
        if (Array.isArray(data)) {
          return data.filter(item => item && item.toString().trim()).join('\n');
        }
        
        // If it's a string, check if it contains commas (multiple items)
        if (typeof data === 'string') {
          const items = data.split(',').map(item => item.trim()).filter(item => item);
          return items.length > 1 ? items.join('\n') : data;
        }
        
        // For any other type, convert to string
        return data.toString();
      };
      
      const containerOrPackage = normalizeContainerPackage(invoice.containerNumber || invoice.packageCount);
      const portOrAirport = invoice.portOfLoading || invoice.airportOfDestination || '';
      
      baseColumns.push(blNumberOrAwb, containerOrPackage, portOrAirport);
    } else if (invoice) {
      // For company ledger, just add BL/AWB and port
      const blNumberOrAwb = invoice.blNumber || invoice.awbNumber || '';
      const portOfDestination = invoice.portOfDestination || '';
      baseColumns.push(blNumberOrAwb, portOfDestination);
    } else {
      // Add empty columns to maintain structure
      if (config.includeShippingDetails) {
        baseColumns.push('', '', '');
      } else {
        baseColumns.push('', '');
      }
    }

    // Add description - convert opening balance to uppercase
    const description = isOpeningBalance ? (entry.description || '').toUpperCase() : (entry.description || '');
    baseColumns.push(description);

    // Add debit and credit
    baseColumns.push(
      entry.debit > 0 ? Math.round(entry.debit).toLocaleString() : '',
      entry.credit > 0 ? Math.round(entry.credit).toLocaleString() : ''
    );

    // Add balance
    baseColumns.push(Math.round(effectiveBalance).toLocaleString());

    // Add days outstanding and remarks for client ledger
    if (config.includeDaysOutstanding) {
      let daysOutstanding = '';
      
      // For "both" client type, show "-" for import invoices
      if (clientType === 'both' && entry.type === 'invoice' && entry.invoiceId) {
        const invType = invoiceTypeMap.get(entry.invoiceId);
        if (invType === 'import') {
          daysOutstanding = '—';
        } else if ((entry as any).remarks !== 'adjusted') {
          const today = new Date();
          const start = new Date(entry.date);
          const diff = Math.floor((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
          daysOutstanding = diff > 0 ? `+${diff}` : '';
        }
      } else if (entry.type === 'invoice' && (entry as any).remarks !== 'adjusted') {
        const today = new Date();
        const start = new Date(entry.date);
        const diff = Math.floor((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        daysOutstanding = diff > 0 ? `+${diff}` : '';
      }
      
      baseColumns.push(daysOutstanding);
    }

    if (config.includeRemarks) {
      let remarks = '';
      
      // For "both" client type, show "-" for import invoices
      if (clientType === 'both' && entry.type === 'invoice' && entry.invoiceId) {
        const invType = invoiceTypeMap.get(entry.invoiceId);
        if (invType === 'import') {
          remarks = '—';
        } else {
          remarks = ((entry as any).remarks || 'outstand').toUpperCase();
        }
      } else if (entry.type === 'invoice') {
        remarks = ((entry as any).remarks || 'outstand').toUpperCase();
      }
      
      baseColumns.push(remarks);
    }

    return baseColumns;
  });

  // Add ledger table
  yPosition += 5;
  const availableWidth = pageWidth - margin * 2;

  // Define table headers and column styles based on ledger type
  let headers: string[];
  let columnStyles: any;
  let totalUnits: number;

  if (config.type === 'client') {
    // Determine majority shipping method for header labels
    const airCount = invoices.filter((inv: any) => inv.shippingMethod === 'air').length;
    const seaCount = invoices.filter((inv: any) => inv.shippingMethod === 'sea').length;
    const majorityShippingMethod: 'air' | 'sea' = airCount > seaCount ? 'air' : 'sea';

    // Build headers dynamically based on config
    headers = [
      'DATE',
      'INVOICE #',
      majorityShippingMethod === 'sea' ? 'BL #' : 'AWB #',
      majorityShippingMethod === 'sea' ? 'CONTAINER' : 'PACKAGE',
      majorityShippingMethod === 'sea' ? 'PORT OF LOADING' : 'AIRPORT',
      'DESCRIPTION',
      'DEBIT',
      'CREDIT',
      'BALANCE'
    ];

    // Conditionally add Days Outstand and Remarks columns
    if (config.includeDaysOutstanding) {
      headers.push('DAYS OUTSTAND');
    }
    if (config.includeRemarks) {
      headers.push('REMARKS');
    }

    // Calculate column styles based on which columns are included
    if (config.includeDaysOutstanding && config.includeRemarks) {
      // All columns included - original proportions
      totalUnits = 7 + 8 + 10 + 9 + 10 + 15 + 7 + 7 + 8 + 8 + 8; // 97
      columnStyles = {
        0: { cellWidth: (7 / totalUnits) * availableWidth }, // Date
        1: { cellWidth: (8 / totalUnits) * availableWidth }, // Invoice #
        2: { cellWidth: (10 / totalUnits) * availableWidth }, // BL No./AWB
        3: { cellWidth: (9 / totalUnits) * availableWidth }, // Container No./Package Count
        4: { cellWidth: (10 / totalUnits) * availableWidth }, // Port of Loading/Airport
        5: { cellWidth: (15 / totalUnits) * availableWidth }, // Description
        6: { cellWidth: (7 / totalUnits) * availableWidth }, // Debit
        7: { cellWidth: (7 / totalUnits) * availableWidth }, // Credit
        8: { cellWidth: (8 / totalUnits) * availableWidth }, // Balance
        9: { cellWidth: (8 / totalUnits) * availableWidth }, // Days Outstand
        10: { cellWidth: (8 / totalUnits) * availableWidth } // Remarks
      };
    } else {
      // Without Days Outstand and Remarks - redistribute space
      totalUnits = 8 + 10 + 12 + 11 + 12 + 18 + 8 + 8 + 10; // 97
      columnStyles = {
        0: { cellWidth: (8 / totalUnits) * availableWidth }, // Date
        1: { cellWidth: (10 / totalUnits) * availableWidth }, // Invoice #
        2: { cellWidth: (12 / totalUnits) * availableWidth }, // BL No./AWB
        3: { cellWidth: (11 / totalUnits) * availableWidth }, // Container No./Package Count
        4: { cellWidth: (12 / totalUnits) * availableWidth }, // Port of Loading/Airport
        5: { cellWidth: (18 / totalUnits) * availableWidth }, // Description (wider)
        6: { cellWidth: (8 / totalUnits) * availableWidth }, // Debit
        7: { cellWidth: (8 / totalUnits) * availableWidth }, // Credit
        8: { cellWidth: (10 / totalUnits) * availableWidth } // Balance
      };
    }
  } else {
    headers = ['DATE', 'INVOICE #', 'BL NO./AWB', 'PORT OF DESTINATION', 'DESCRIPTION', 'DEBIT', 'CREDIT', 'BALANCE'];
    
    // Column proportions: 10, 12, 14, 17, 17, 9, 9, 11 out of 99 total
    totalUnits = 10 + 12 + 14 + 17 + 17 + 9 + 9 + 11; // 99
    columnStyles = {
      0: { cellWidth: (10 / totalUnits) * availableWidth }, // Date
      1: { cellWidth: (12 / totalUnits) * availableWidth }, // Invoice #
      2: { cellWidth: (16 / totalUnits) * availableWidth }, // BL No./AWB
      3: { cellWidth: (15 / totalUnits) * availableWidth }, // Port of Destination
      4: { cellWidth: (17 / totalUnits) * availableWidth }, // Description
      5: { cellWidth: (9 / totalUnits) * availableWidth }, // Debit
      6: { cellWidth: (9 / totalUnits) * availableWidth }, // Credit
      7: { cellWidth: (11 / totalUnits) * availableWidth } // Balance
    };
  }

  autoTable(doc, {
    startY: yPosition,
    head: [headers],
    body: ledgerData,
    theme: 'grid',
    headStyles: {
      fillColor: [252, 180, 116],
      textColor: [0, 0, 0],
      fontSize: 9,
      font: 'calibri'
    },
    styles: {
      fontSize: 8,
      cellPadding: 2,
      halign: 'center',
      font: 'calibri'
    },
    margin: { left: margin, right: margin },
    tableWidth: availableWidth,
    columnStyles,
    didParseCell: function (data) {
      if (config.type === 'client') {
        // Bold Balance column cells for client ledger with larger font
        if (data.section === 'body' && data.column.index === 8) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 9; // Slightly larger font
          data.cell.styles.textColor = [247, 16, 0]; // Dark red color
        }
        // Bold Balance header with larger font
        if (data.section === 'head' && data.column.index === 8) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 10; // Slightly larger font for header
          data.cell.styles.textColor = [0, 0, 0]; // Black color for header
        }
        // Color code Remarks column (only if included)
        if (config.includeRemarks) {
          // Calculate the remarks column index dynamically
          const remarksColumnIndex = config.includeDaysOutstanding ? 10 : 9;
          if (data.section === 'body' && data.column.index === remarksColumnIndex) {
            const val = String(data.cell.text?.[0] || '').toUpperCase();
            if (val === 'OUTSTAND') {
              data.cell.styles.fillColor = [254, 226, 226]; // light red
              data.cell.styles.textColor = [247, 16, 0]; // dark red
            } else if (val === 'ADJUSTED') {
              data.cell.styles.fillColor = [198, 239, 206]; // light green
              data.cell.styles.textColor = [0, 97, 0]; // dark green
            }
          }
        }
      } else {
        // Style the balance column to be bold for company ledger with larger font
        if (data.column.index === 7) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = data.section === 'head' ? 10 : 9; // Larger font for header, slightly larger for body
          data.cell.styles.textColor = data.section === 'head' ? [0, 0, 0] : [252, 3, 3]; // Black for header, red for data
        }
      }
    }
  });

  // Add notes to the last page after table generation
  const pageHeight = doc.internal.pageSize.height;
  const notesY = pageHeight - 20;
  
  // Set font for notes
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  
  // Add system note
  const systemNote = 'This is system generated ledger and does not require any signature.';
  doc.text(systemNote, margin + 2, notesY + 6);
  
  // Add content note
  const contentNote = 'Content of the ledger will be considered correct if no errors are reported within 2 working days from ledger date.';
  doc.text(contentNote, margin + 2, notesY + 11);

  return doc;
};

// Legacy function names for backward compatibility
export const generateClientLedgerPDF = async (
  clientLedger: ClientLedger, 
  invoices: any[], 
  selectedLogisticsCompany?: string,
  clientType?: 'import' | 'export' | 'both'
): Promise<jsPDF> => {
  return generateLedgerPDF(clientLedger, invoices, selectedLogisticsCompany, 'client', clientType);
};

export const generateCompanyLedgerPDF = async (companyLedger: ClientLedger, invoices: any[], selectedLogisticsCompany?: string): Promise<jsPDF> => {
  return generateLedgerPDF(companyLedger, invoices, selectedLogisticsCompany, 'company');
};

// Function to open PDF in new tab
export const openPDFInNewTab = (pdf: jsPDF, filename: string = 'document.pdf'): void => {
  const pdfBlob = pdf.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const newWindow = window.open(pdfUrl, '_blank');
  if (!newWindow) {
    // Fallback: download the PDF if popup is blocked
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

// Excel export functions
export const generateLedgerExcel = (
  ledger: ClientLedger, 
  invoices: any[], 
  selectedLogisticsCompany?: string,
  ledgerType: LedgerType = 'client',
  clientType?: 'import' | 'export' | 'both'
): void => {
  const config = { ...LEDGER_CONFIGS[ledgerType] };
  
  // For import clients, hide days outstanding and remarks
  if (clientType === 'import') {
    config.includeDaysOutstanding = false;
    config.includeRemarks = false;
  }
  
  // Create a new workbook
  const workbook = XLSX.utils.book_new();
  
  // Prepare data for Excel
  let runningBalance = 0;
  const invoiceMap = new Map(invoices.map(inv => [inv.id, inv]));
  
  // Create a map of invoice ID to invoice type for "both" client type handling
  const invoiceTypeMap = new Map(invoices.map(inv => [inv.id, inv.invoiceType]));
  
  // Separate opening balance entries from regular entries
  const openingBalanceEntries = ledger.entries.filter(entry => 
    entry.type === 'adjustment' && entry.description === 'Opening Balance'
  );
  const regularEntries = ledger.entries.filter(entry => 
    !(entry.type === 'adjustment' && entry.description === 'Opening Balance')
  );

  // Sort regular entries by date
  regularEntries.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Combine opening balance entries first, then regular entries
  const sortedEntries = [...openingBalanceEntries, ...regularEntries];
  
  // Prepare ledger data
  const ledgerData = sortedEntries.map(entry => {
    const invoice = entry.invoiceId ? invoiceMap.get(entry.invoiceId) : null;

    // Calculate running balance for client ledger
    if (config.type === 'client') {
      runningBalance += entry.credit - entry.debit;
    }

    const effectiveBalance = config.type === 'client' 
      ? (typeof (entry as any).balance === 'number' ? (entry as any).balance : runningBalance)
      : entry.balance;

    // For opening balance entries, don't show date
    const isOpeningBalance = entry.type === 'adjustment' && entry.description === 'Opening Balance';

    // Base columns that are common to both types
    const baseColumns = [
      isOpeningBalance ? '' : (entry.date?.toLocaleDateString ? entry.date.toLocaleDateString() : formatDate(entry.date)), // Date (empty for opening balance)
      invoice ? (invoice.invoiceNumber || '') : '', // Invoice #
    ];

    // Add shipping-specific columns for client ledger
    if (config.includeShippingDetails && invoice) {
      const blNumberOrAwb = invoice.blNumber || invoice.awbNumber || '';
      
      // Enhanced function to handle multiple container numbers or package counts
      const normalizeContainerPackage = (data: any) => {
        if (!data) return '';
        
        // If it's already an array, join with line breaks
        if (Array.isArray(data)) {
          return data.filter(item => item && item.toString().trim()).join('\n');
        }
        
        // If it's a string, check if it contains commas (multiple items)
        if (typeof data === 'string') {
          const items = data.split(',').map(item => item.trim()).filter(item => item);
          return items.length > 1 ? items.join('\n') : data;
        }
        
        // For any other type, convert to string
        return data.toString();
      };
      
      const containerOrPackage = normalizeContainerPackage(invoice.containerNumber || invoice.packageCount);
      const portOrAirport = invoice.portOfLoading || invoice.airportOfDestination || '';
      
      baseColumns.push(blNumberOrAwb, containerOrPackage, portOrAirport);
    } else if (invoice) {
      // For company ledger, just add BL/AWB and port
      const blNumberOrAwb = invoice.blNumber || invoice.awbNumber || '';
      const portOfDestination = invoice.portOfDestination || '';
      baseColumns.push(blNumberOrAwb, portOfDestination);
    } else {
      // Add empty columns to maintain structure
      if (config.includeShippingDetails) {
        baseColumns.push('', '', '');
      } else {
        baseColumns.push('', '');
      }
    }

    // Add description - convert opening balance to uppercase
    const description = isOpeningBalance ? (entry.description || '').toUpperCase() : (entry.description || '');
    baseColumns.push(description);

    // Add debit and credit
    baseColumns.push(
      entry.debit > 0 ? entry.debit : '',
      entry.credit > 0 ? entry.credit : ''
    );

    // Add balance
    baseColumns.push(effectiveBalance);

    // Add days outstanding and remarks for client ledger
    if (config.includeDaysOutstanding) {
      let daysOutstanding: string | number = '';
      
      // For "both" client type, show "-" for import invoices
      if (clientType === 'both' && entry.type === 'invoice' && entry.invoiceId) {
        const invType = invoiceTypeMap.get(entry.invoiceId);
        if (invType === 'import') {
          daysOutstanding = '—';
        } else if ((entry as any).remarks !== 'adjusted') {
          const today = new Date();
          const start = new Date(entry.date);
          const diff = Math.floor((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
          daysOutstanding = diff > 0 ? diff : '';
        }
      } else if (entry.type === 'invoice' && (entry as any).remarks !== 'adjusted') {
        const today = new Date();
        const start = new Date(entry.date);
        const diff = Math.floor((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        daysOutstanding = diff > 0 ? diff : '';
      }
      
      baseColumns.push(daysOutstanding);
    }

    if (config.includeRemarks) {
      let remarks = '';
      
      // For "both" client type, show "-" for import invoices
      if (clientType === 'both' && entry.type === 'invoice' && entry.invoiceId) {
        const invType = invoiceTypeMap.get(entry.invoiceId);
        if (invType === 'import') {
          remarks = '—';
        } else {
          remarks = ((entry as any).remarks || 'outstand').toUpperCase();
        }
      } else if (entry.type === 'invoice') {
        remarks = ((entry as any).remarks || 'outstand').toUpperCase();
      }
      
      baseColumns.push(remarks);
    }

    return baseColumns;
  });

  // Define headers based on ledger type
  let headers: string[];
  
  if (config.type === 'client') {
    // Determine majority shipping method for header labels
    const airCount = invoices.filter((inv: any) => inv.shippingMethod === 'air').length;
    const seaCount = invoices.filter((inv: any) => inv.shippingMethod === 'sea').length;
    const majorityShippingMethod: 'air' | 'sea' = airCount > seaCount ? 'air' : 'sea';

    headers = [
      'DATE',
      'INVOICE #',
      majorityShippingMethod === 'sea' ? 'BL #' : 'AWB #',
      majorityShippingMethod === 'sea' ? 'CONTAINER' : 'PACKAGE',
      majorityShippingMethod === 'sea' ? 'PORT OF LOADING' : 'AIRPORT',
      'DESCRIPTION',
      'DEBIT',
      'CREDIT',
      'BALANCE'
    ];

    // Conditionally add Days Outstand and Remarks columns
    if (config.includeDaysOutstanding) {
      headers.push('DAYS OUTSTAND');
    }
    if (config.includeRemarks) {
      headers.push('REMARKS');
    }
  } else {
    headers = ['DATE', 'INVOICE #', 'BL NO./AWB', 'PORT OF DESTINATION', 'DESCRIPTION', 'DEBIT', 'CREDIT', 'BALANCE'];
  }

  // Create worksheet data
  const worksheetData = [headers, ...ledgerData];
  
  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  
  // Apply cell formatting for line breaks in container/package column
  if (config.type === 'client') {
    const containerColumnIndex = 3; // CONTAINER/PACKAGE column (0-indexed)
    
    // Apply text wrapping to all cells in the container/package column
    for (let row = 1; row < worksheetData.length; row++) { // Skip header row
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: containerColumnIndex });
      if (!worksheet[cellAddress]) continue;
      
      // Ensure the cell value is properly set
      const cellValue = worksheetData[row][containerColumnIndex];
      if (cellValue && typeof cellValue === 'string' && cellValue.includes('\n')) {
        worksheet[cellAddress].v = cellValue; // Ensure the value is set
        worksheet[cellAddress].t = 's'; // Set type as string
        
        // Set cell style for text wrapping
        worksheet[cellAddress].s = {
          ...worksheet[cellAddress].s,
          alignment: {
            wrapText: true,
            vertical: 'top',
            horizontal: 'left'
          }
        };
      }
    }
  }
  
  // Set column widths
  let columnWidths: any[];
  
  if (config.type === 'client') {
    columnWidths = [
      { wch: 12 }, // DATE
      { wch: 15 }, // INVOICE #
      { wch: 18 }, // BL #/AWB #
      { wch: 20 }, // CONTAINER/PACKAGE (increased for multiple lines)
      { wch: 20 }, // PORT OF LOADING/AIRPORT
      { wch: 30 }, // DESCRIPTION
      { wch: 12 }, // DEBIT
      { wch: 12 }, // CREDIT
      { wch: 15 }  // BALANCE
    ];
    
    // Conditionally add Days Outstand and Remarks column widths
    if (config.includeDaysOutstanding) {
      columnWidths.push({ wch: 12 }); // DAYS OUTSTAND
    }
    if (config.includeRemarks) {
      columnWidths.push({ wch: 12 }); // REMARKS
    }
  } else {
    columnWidths = [
      { wch: 12 }, // DATE
      { wch: 15 }, // INVOICE #
      { wch: 18 }, // BL NO./AWB
      { wch: 25 }, // PORT OF DESTINATION
      { wch: 30 }, // DESCRIPTION
      { wch: 12 }, // DEBIT
      { wch: 12 }, // CREDIT
      { wch: 15 }  // BALANCE
    ];
  }
  
  worksheet['!cols'] = columnWidths;
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Ledger');
  
  // Generate filename
  const sanitizedClientName = ledger.clientName.replace(/[^a-zA-Z0-9]/g, '');
  const date = new Date().toISOString().split('T')[0];
  const filename = `${ledgerType === 'client' ? 'Client' : 'Company'}_Ledger_${sanitizedClientName}_${date}.xlsx`;
  
  // Save the file
  XLSX.writeFile(workbook, filename);
};

// Legacy function names for backward compatibility
export const generateClientLedgerExcel = (
  clientLedger: ClientLedger, 
  invoices: any[], 
  selectedLogisticsCompany?: string,
  clientType?: 'import' | 'export' | 'both'
): void => {
  return generateLedgerExcel(clientLedger, invoices, selectedLogisticsCompany, 'client', clientType);
};

export const generateCompanyLedgerExcel = (companyLedger: ClientLedger, invoices: any[], selectedLogisticsCompany?: string): void => {
  return generateLedgerExcel(companyLedger, invoices, selectedLogisticsCompany, 'company');
};
